"use client";

import { useState, useEffect, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getMorningBrief, logMood, updateTask, carryForwardTask, suggestSmallerStep } from "@/lib/api";
import { useDataRefresh } from "@/lib/store";
import type { MorningBrief, CarryForwardOutcome } from "@/lib/api";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

interface Props {
  onDismiss: () => void;
  onOpenPlan?: () => void;
  displayName?: string | null;
  completedToday?: number;
  // Optional pre-fetched brief — when provided, the popup skips its
  // own getMorningBrief() round-trip and renders instantly. Falls back
  // to fetching if not provided so the component stays standalone.
  prefetchedBrief?: MorningBrief | null;
}

const MOOD_EMOJIS: Array<{ score: number; emoji: string; label: string }> = [
  { score: 1, emoji: "😞", label: "Drained" },
  { score: 2, emoji: "😕", label: "Off" },
  { score: 3, emoji: "😐", label: "OK" },
  { score: 4, emoji: "😊", label: "Good" },
  { score: 5, emoji: "😄", label: "Great" },
];

interface TriageOption {
  outcome: CarryForwardOutcome | "completed";
  emoji: string;
  label: string;
}

const TRIAGE_OPTIONS: TriageOption[] = [
  { outcome: "completed", emoji: "✓", label: "Done" },
  { outcome: "tomorrow", emoji: "🌙", label: "Tomorrow" },
  { outcome: "made_smaller", emoji: "✂", label: "Smaller" },
  { outcome: "not_now", emoji: "🗑", label: "Release" },
];

function firstName(displayName?: string | null): string | null {
  if (!displayName) return null;
  const trimmed = displayName.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0];
}

function buildEveningRecap(brief: MorningBrief, completedToday: number): string | null {
  // Keep one short, factual line. Surfaces wins + remaining count without
  // pretending the LLM said anything; if a real ai_brief lands later we
  // can swap this for the trimmed first sentence.
  const remaining = brief.top_tasks?.length ?? 0;
  if (completedToday > 0 && remaining > 0) {
    return `${completedToday} thing${completedToday === 1 ? "" : "s"} landed today, ${remaining} still open.`;
  }
  if (completedToday > 0) {
    return `${completedToday} thing${completedToday === 1 ? "" : "s"} landed today. Clean close.`;
  }
  if (remaining > 0) {
    return "Quiet day on the board. Wrap, then let tomorrow restart fresh.";
  }
  return null;
}

export default function EveningWrapPopup({
  onDismiss,
  onOpenPlan,
  displayName,
  completedToday = 0,
  prefetchedBrief = null,
}: Props) {
  const [brief, setBrief] = useState<MorningBrief | null>(prefetchedBrief);
  const [mood, setMood] = useState(3);
  const [failed, setFailed] = useState(false);
  // Per-task action state: id → outcome that was applied (so the chip
  // shows as picked and the row collapses out optimistically).
  const [routedTasks, setRoutedTasks] = useState<Record<string, TriageOption["outcome"]>>({});
  const [routingId, setRoutingId] = useState<string | null>(null);
  // Smaller-edit inline state. When the user taps ✂ Smaller, the row's
  // chips are replaced by an input pre-filled (or placeholder'd) with
  // the AI-suggested tinier title. The user can accept-as-is (Enter on
  // empty input → use suggestion), edit before commit, or Escape to
  // back out without committing.
  const [smallerEditId, setSmallerEditId] = useState<string | null>(null);
  const [smallerSuggestion, setSmallerSuggestion] = useState<string | null>(null);
  const [smallerSuggestionLoading, setSmallerSuggestionLoading] = useState(false);
  const [smallerDraft, setSmallerDraft] = useState("");
  // Brief in-row confirmation after a route action — strikethrough +
  // outcome badge held for 1s so the user can see something happened
  // before the row collapses. Without this, rows with similar titles
  // slide in seamlessly and the action looks like it did nothing.
  const [actionFeedback, setActionFeedback] = useState<{
    taskId: string;
    label: string;
  } | null>(null);

  const outcomeLabel = (outcome: TriageOption["outcome"]): string => {
    switch (outcome) {
      case "completed":
        return "Marked done";
      case "tomorrow":
        return "Moved to tomorrow";
      case "made_smaller":
        return "Saved smaller step";
      case "not_now":
        return "Released for now";
      default:
        return "Saved";
    }
  };

  useEffect(() => {
    if (prefetchedBrief) return; // already supplied by parent
    getMorningBrief({ phase: "evening" })
      .then(setBrief)
      .catch(() => setFailed(true));
  }, [prefetchedBrief]);

  const [moodError, setMoodError] = useState<string | null>(null);

  const persistMood = async (): Promise<boolean> => {
    try {
      await logMood({ mood_score: Math.round(mood), energy_level: 3 });
      return true;
    } catch (err) {
      const detail = err instanceof Error ? err.message : "Could not save mood.";
      setMoodError(detail);
      return false;
    }
  };

  const handleClose = async () => {
    const ok = await persistMood();
    if (!ok) {
      // Don't auto-dismiss on failure so the user sees the error and can retry.
      return;
    }
    onDismiss();
  };

  const handleOpenPlan = async () => {
    const ok = await persistMood();
    if (!ok) return;
    onOpenPlan?.();
  };

  const handleRouteTask = async (taskId: string, option: TriageOption) => {
    // Smaller is special: instead of fire-and-forget, open the inline
    // editor with an AI-suggested tinier title. The actual commit
    // happens on Enter (commitSmaller) once the user confirms.
    if (option.outcome === "made_smaller") {
      openSmallerEditor(taskId);
      return;
    }
    setRoutingId(taskId);
    try {
      if (option.outcome === "completed") {
        await updateTask(taskId, { status: "completed" });
        // Backend cascades the completion (ADR-0005); fire notifiers so Today /
        // Goals / Progress reflect it without waiting for a tab remount.
        useDataRefresh.getState().notifyTaskChange();
        useDataRefresh.getState().notifyGoalChange();
      } else {
        await carryForwardTask(taskId, { outcome: option.outcome });
        useDataRefresh.getState().notifyTaskChange();
      }
      // Show in-row confirmation for 1s, then collapse the row by
      // marking it routed. Lets the user see the action landed even
      // when neighboring rows have similar titles.
      setActionFeedback({ taskId, label: outcomeLabel(option.outcome) });
      setTimeout(() => {
        setRoutedTasks((current) => ({ ...current, [taskId]: option.outcome }));
        setActionFeedback((current) =>
          current?.taskId === taskId ? null : current,
        );
      }, 1800);
    } catch {
      setActionFeedback({ taskId, label: "Couldn't save — tap again to retry" });
      setTimeout(() => {
        setActionFeedback((current) =>
          current?.taskId === taskId ? null : current,
        );
      }, 2200);
    } finally {
      setRoutingId(null);
    }
  };

  const openSmallerEditor = (taskId: string) => {
    setSmallerEditId(taskId);
    setSmallerDraft("");
    setSmallerSuggestion(null);
    setSmallerSuggestionLoading(true);
    void suggestSmallerStep(taskId)
      .then((response) => {
        setSmallerSuggestion(response.suggested_title);
      })
      .catch(() => {
        setSmallerSuggestion(null);
      })
      .finally(() => {
        setSmallerSuggestionLoading(false);
      });
  };

  const cancelSmallerEditor = () => {
    setSmallerEditId(null);
    setSmallerDraft("");
    setSmallerSuggestion(null);
    setSmallerSuggestionLoading(false);
  };

  const commitSmaller = async (taskId: string) => {
    // Empty draft → user accepted the AI suggestion as-is. If neither
    // exists, fall back to no smaller_title (backend will auto-prefix).
    const finalTitle = smallerDraft.trim() || smallerSuggestion?.trim() || undefined;
    setRoutingId(taskId);
    try {
      await carryForwardTask(taskId, {
        outcome: "made_smaller",
        smaller_title: finalTitle,
      });
      setRoutedTasks((current) => ({ ...current, [taskId]: "made_smaller" }));
      cancelSmallerEditor();
    } catch {
      // keep editor open so user can retry
    } finally {
      setRoutingId(null);
    }
  };

  // Empty / failure: still show a polite goodbye card.
  if (failed || !brief) {
    if (!failed && !brief) return null;
    return (
      <PopupOverlay onDismiss={handleClose}>
        <div data-testid="evening-wrap-empty" className="relative">
          <div className="text-center">
            <span className="text-3xl">🌇</span>
            <h2 id="evening-wrap-title" className="mt-2 font-serif text-2xl font-medium tracking-tight text-text">
              {firstName(displayName) ? `Good evening, ${firstName(displayName)}` : "Good evening"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Nothing more to triage tonight. Rest well.
            </p>
          </div>
          <div className="mt-5 flex justify-center">
            <button
              onClick={() => void handleClose()}
              className="rounded-xl bg-forest px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-med"
            >
              Close day
            </button>
          </div>
        </div>
      </PopupOverlay>
    );
  }

  const recap = buildEveningRecap(brief, completedToday);
  const remainingTasks = (brief.top_tasks ?? [])
    .filter((task) => !routedTasks[task.id])
    .slice(0, 3);

  return (
    <PopupOverlay onDismiss={handleClose}>
      <div data-testid="evening-wrap-popup" className="relative">
        <div className="text-center mb-4">
          <span className="text-3xl">🌇</span>
          <h2 id="evening-wrap-title" className="mt-2 text-lg font-bold text-text">
            {firstName(displayName) ? `Good evening, ${firstName(displayName)}` : "Good evening"}
          </h2>
          {completedToday > 0 && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber/15 px-2.5 py-1 text-[11px] font-semibold text-amber">
              <span aria-hidden="true">🌟</span>
              <span>
                {completedToday} landed today
              </span>
            </div>
          )}
        </div>

        {recap && (
          <p className="mb-4 text-center text-sm leading-relaxed text-text-light">
            {recap}
          </p>
        )}

        {remainingTasks.length > 0 && (
          <div className="mb-4" data-testid="evening-wrap-triage">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
              Still open — pick a path
            </p>
            <ul className="space-y-2">
              {remainingTasks.map((task) => {
                const isRouting = routingId === task.id;
                const isEditingSmaller = smallerEditId === task.id;
                const feedback =
                  actionFeedback?.taskId === task.id ? actionFeedback : null;
                const titleStruck = Boolean(feedback);
                return (
                  <li
                    key={task.id}
                    className="rounded-xl border border-border bg-card px-3 py-2.5"
                  >
                    <p
                      className={`text-sm leading-snug ${
                        titleStruck ? "text-muted line-through" : "text-text"
                      }`}
                    >
                      {sanitizeSurfaceTitle(task.title)}
                    </p>

                    {feedback ? (
                      <p
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-forest-med/10 px-2.5 py-0.5 text-[11px] font-medium text-forest-med"
                        data-testid={`route-feedback-${task.id}`}
                      >
                        <span aria-hidden="true">→</span>
                        {feedback.label}
                      </p>
                    ) : isEditingSmaller ? (
                      <div className="mt-2" data-testid={`smaller-input-${task.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-base" aria-hidden="true">✂</span>
                          <input
                            type="text"
                            autoFocus
                            value={smallerDraft}
                            placeholder={
                              smallerSuggestionLoading
                                ? "Thinking of a tinier step…"
                                : smallerSuggestion ?? "Type a tinier next step"
                            }
                            onChange={(event) => setSmallerDraft(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                void commitSmaller(task.id);
                              } else if (event.key === "Escape") {
                                event.preventDefault();
                                cancelSmallerEditor();
                              }
                            }}
                            disabled={isRouting}
                            className="flex-1 rounded-lg border border-border bg-bg px-2.5 py-1.5 text-sm text-text placeholder:text-muted/70 focus:border-forest-med/40 focus:outline-none disabled:opacity-60"
                          />
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-[10px] text-muted">
                            Enter to commit · Esc to cancel · empty + Enter accepts AI suggestion
                          </p>
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={cancelSmallerEditor}
                              disabled={isRouting}
                              className="rounded-full border border-border bg-card px-2.5 py-0.5 text-[10px] font-medium text-text-light transition-colors hover:border-rose/30 hover:text-rose disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void commitSmaller(task.id)}
                              disabled={isRouting}
                              className="rounded-full bg-forest-med px-3 py-0.5 text-[10px] font-medium text-white transition-colors hover:bg-forest disabled:opacity-50"
                            >
                              {isRouting ? "Saving…" : "Save smaller"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {TRIAGE_OPTIONS.map((option) => (
                          <button
                            key={option.outcome}
                            type="button"
                            onClick={() => void handleRouteTask(task.id, option)}
                            disabled={isRouting}
                            aria-label={`${option.label} ${task.title}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-bg px-2.5 py-1 text-[11px] font-medium text-text-light transition-colors hover:border-forest-med/40 hover:text-text disabled:opacity-50"
                          >
                            <span aria-hidden="true">{option.emoji}</span>
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mb-4">
          <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
            How was today?
          </p>
          <div className="flex items-center justify-between gap-1">
            {MOOD_EMOJIS.map((option) => {
              const active = mood === option.score;
              return (
                <motion.button
                  key={option.score}
                  type="button"
                  onClick={() => setMood(option.score)}
                  aria-label={option.label}
                  aria-pressed={active}
                  whileTap={{ scale: 0.85 }}
                  animate={{ scale: active ? 1.15 : 1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 18 }}
                  className={`flex h-11 w-11 items-center justify-center rounded-full text-2xl ${
                    active
                      ? "bg-forest-med/10 ring-2 ring-forest-med/40"
                      : "opacity-60 hover:opacity-100 hover:bg-secondary/40"
                  }`}
                >
                  {option.emoji}
                </motion.button>
              );
            })}
          </div>
        </div>

        {moodError && (
          <p
            className="mb-3 rounded-xl border border-rose/30 bg-rose/5 px-3 py-2 text-[11px] font-medium text-rose"
            role="alert"
            aria-live="polite"
          >
            {moodError} — tap Close day to retry.
          </p>
        )}

        <div className="flex gap-3">
          {onOpenPlan && (
            <button
              onClick={() => void handleOpenPlan()}
              className="flex-1 rounded-xl border border-border py-2.5 text-sm font-medium text-text transition-colors hover:border-forest/25 hover:text-forest-med"
            >
              Open Plan
            </button>
          )}
          <button
            onClick={() => void handleClose()}
            className={`${onOpenPlan ? "flex-1" : "w-full"} rounded-xl bg-forest py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-med`}
          >
            Close day
          </button>
        </div>
      </div>
    </PopupOverlay>
  );
}

interface PopupOverlayProps {
  onDismiss: () => void;
  children: ReactNode;
}

// Hoisted out (same pattern as MorningBriefPopup) so identity stays
// stable across mood / triage state changes — otherwise AnimatePresence
// would replay the entrance every render.
function PopupOverlay({ onDismiss, children }: PopupOverlayProps) {
  return (
    <AnimatePresence>
      <motion.div
        key="evening-wrap-overlay"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 backdrop-blur-md px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evening-wrap-title"
        onClick={onDismiss}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
      >
        <motion.div
          className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-border bg-gradient-to-b from-clay/20 via-cream to-cream px-5 py-5 shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ type: "spring", stiffness: 220, damping: 22, mass: 0.6 }}
        >
          {/* Sunset glow at top — warmer / pinker than the morning sunrise. */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-rose/15 via-amber/8 to-transparent" />
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="relative">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
